import {
  InvalidateOptions,
  InvalidateQueryFilters,
  useIsFetching,
  useIsMutating,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryOptions,
  UseSuspenseQueryResult,
} from "@tanstack/react-query"

import { queryClient } from "./client"

/**
 * A generic function-like type.
 *
 * In theory, we could replace FunctionLike with concrete signatures like:
 * `(query?: PageRequest) => Promise<AuditTrailResponsePageResponse>`
 * but that only works for one method in one service. To support all methods across all services,
 * we need a flexible abstraction - and `any` is the only viable choice here.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FunctionLike = (...args: any[]) => any

type GetReturnType<T extends FunctionLike> = Awaited<ReturnType<T>>

type ExtendSuspenseQueryOptions<T extends FunctionLike> = Omit<
  UseSuspenseQueryOptions<ReturnType<T>>,
  "queryKey" | "useQuery"
>
export type ExtendQueryOptions<T extends FunctionLike> = Omit<
  UseQueryOptions<ReturnType<T>>,
  "queryKey" | "useQuery"
> & {
  /**
   * @deprecated Please use useSuspenseQuery instead of setting this option. You can use this option for conditional suspense, but watch out! Some features might not work as expected. (for example `throwOnError: false` will be ignored)
   *
   * If true, the query will be treated as a suspense query. If you don't use `enabled`, use `useSuspenseQuery` directly.
   *
   * @see https://github.com/TanStack/query/discussions/6206
   *
   * This is a bit of a hack to allow optional suspense queries. The reason this works is that under the hood useQuery calls useBaseQuery which is the same as useSuspenseQuery.
   * So we can pass the hidden option `suspense: true` to make it behave like a suspense query.
   */
  suspense?: true
}

type ExtendMutationOptions<T extends FunctionLike> = Omit<
  UseMutationOptions<GetReturnType<T>, unknown, Parameters<T>>,
  "mutationKey" | "mutationFn"
>

/**
 * A double nested object of services.
 *
 * `[TKey1 in string]` is used intentionally to encapsulate a service.
 *
 * `[TKey2 in string]: any` is used intentionally to encapsulate
 * every possible service (anything it exports). This is a deliberate trade-off to preserve type safety at the edges
 * while allowing dynamic introspection and inference internally (via the builder).
 */
type ServiceDictionaryType = {
  [TKey1 in string]: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [TKey2 in string]: any
  }
}

/**
 * A query key is a tuple of [serviceName, methodName]
 */
type QueryKey = [string, string]

/**
 * Two level nested proxy to build api calls.
 *
 * Instead of doing some translation logic mapping all methods and building one huge object upfront,
 * we just use 2 proxies to save the keys being used to access the api, and then we perform a lookup on the api dictionary
 * Example: api.key1.key2.someMethod()
 * The benefit of using a proxy here is that we don't need to predefine the api
 * structure, and it will automatically adapt to the services provided. This is also really cheap to create each reflow.
 */
const nestedProxy = (builder: (key: QueryKey) => Record<string, FunctionLike>): unknown =>
  new Proxy(
    {},
    {
      get: (_, key1: string) =>
        new Proxy(
          {},
          {
            get: (_, key2: string) => builder([key1, key2]),
          }
        ),
    }
  )

/**
 * The api object, built from the services
 *
 * Please refer to buildApiFromServices for the actual implementation
 *
 * Q: Looking at this type, why is everything nested like this?
 *
 * A: This allows us to have a trpc-like api structure, where we can do:
 * `api.serviceName.methodName.useQuery(...)` and correctly resolve the methods parameters and return types.
 * This way, we get full type safety and autocompletion for free.
 * We also don't need additional boilerplate to wrap our services.
 */
type Api<TServiceDictionary extends ServiceDictionaryType> = {
  [TKey1 in keyof TServiceDictionary]: {
    [TKey2 in keyof TServiceDictionary[TKey1]]: {
      /**
       * See docs for useQuery: https://tanstack.com/query/v5/docs/react/reference/useQuery
       *
       * @param args params for the query function, followed by an optional options object passed to useQuery
       * @returns The result of useQuery for the given service function
       */
      useQuery: (
        ...args: [
          ...Parameters<TServiceDictionary[TKey1][TKey2]>,
          ExtendQueryOptions<TServiceDictionary[TKey1][TKey2]>?,
        ]
      ) => UseQueryResult<GetReturnType<TServiceDictionary[TKey1][TKey2]>>

      /**
       * See docs for useSuspenseQuery: https://tanstack.com/query/v5/docs/react/reference/useSuspenseQuery
       *
       * @param args params for the query function, followed by an optional options object passed to useSuspenseQuery
       * @returns The result of useSuspenseQuery for the given service function
       */
      useSuspenseQuery: (
        ...args: [
          ...Parameters<TServiceDictionary[TKey1][TKey2]>,
          ExtendSuspenseQueryOptions<TServiceDictionary[TKey1][TKey2]>?,
        ]
      ) => UseSuspenseQueryResult<GetReturnType<TServiceDictionary[TKey1][TKey2]>>

      /**
       * Returns the fully-resolved TanStack Query options object (queryKey, queryFn, and merged
       * service/method/per-call defaults). Feed this into `useSuspenseQueries({ queries: [...] })`
       * to fetch multiple endpoints in parallel under suspense.
       */
      queryOptions: (
        ...args: [
          ...Parameters<TServiceDictionary[TKey1][TKey2]>,
          ExtendSuspenseQueryOptions<TServiceDictionary[TKey1][TKey2]>?,
        ]
      ) => {
        queryKey: readonly unknown[]
        queryFn: () => Promise<GetReturnType<TServiceDictionary[TKey1][TKey2]>>
      }

      /**
       * Use this hook to determine if there are any active fetches for this query (so if any useQuery is loading)
       * @returns true if there are active fetches for this query
       */
      useIsFetching: () => boolean

      /**
       * Use this hook to determine if there are any active mutations for this mutation (so if any useMutation is loading)
       * @returns true if there are active mutations for this mutation key
       */
      useIsMutating: () => boolean

      /**
       * See docs for useMutation: https://tanstack.com/query/v5/docs/react/reference/useMutation
       *
       * @param options options object passed to useMutation
       * @returns The result of useMutation for the given service function
       */
      useMutation: (
        options?: ExtendMutationOptions<TServiceDictionary[TKey1][TKey2]>
      ) => UseMutationResult<
        GetReturnType<TServiceDictionary[TKey1][TKey2]>,
        unknown,
        Parameters<TServiceDictionary[TKey1][TKey2]>
      >

      /**
       * Invalidates queries for the given service function. See docs for invalidateQueries: https://tanstack.com/query/latest/docs/framework/react/guides/query-invalidation
       *
       * Removes queries, but directly fetches active data. This means it instantly begins reloading the data if used in a component visible on
       * the screen.
       *
       * It is important to always invalidate queries after performing mutations that change data, to ensure the UI reflects the latest state.
       *
       * @param args params for the query function, followed by optional InvalidateQueryFilters and InvalidateOptions
       */
      invalidateQueries:
        | ((...args: Parameters<TServiceDictionary[TKey1][TKey2]>) => Promise<void>)
        | ((...args: [...Parameters<TServiceDictionary[TKey1][TKey2]>, InvalidateOptions]) => Promise<void>)
        | ((
            ...args: [...Parameters<TServiceDictionary[TKey1][TKey2]>, InvalidateQueryFilters, InvalidateOptions]
          ) => Promise<void>)

      /**
       * Removes queries for the given service function. See docs for removeQueries: https://tanstack.com/query/latest/docs/framework/react/guides/query-invalidation
       *
       * Removes queries from the cache. Active queries will **NOT** be refetched if they are used in a component visible on the screen.
       *
       * For most use cases, invalidateQueries is preferred.
       *
       * @param args params for the query function, followed by optional QueryFilters
       */
      removeQueries: {
        (): Promise<void>
        (...args: Parameters<TServiceDictionary[TKey1][TKey2]>): Promise<void>
      }
    }
  }
}

/**
 * Builds a trpc-style api out of an existing services.
 *
 * This is done to reduce the boilerplate usually associated with react query, while still maintaining full type safety and autocompletion.
 *
 * This method should be called once in index.ts to build the api object used throughout the app.
 *
 * @param serviceDict An object containing all services to build the api from
 * @returns An api object with methods for each service function
 */
export const buildApiFromServices = <TServiceDictionary extends ServiceDictionaryType>(
  serviceDict: TServiceDictionary
) => {
  /**
   * Resolves the service and function from the key
   */
  const resolveBasic = (key: QueryKey) => {
    const service = serviceDict[key[0]]
    const func = service[key[1]] as FunctionLike

    // Assert that the service and function exist
    if (typeof func !== "function") {
      throw new Error(`${key[1]} does not exist on service ${key[0]} or is not a function`)
    }

    return { service, func }
  }

  /**
   * Resolves the service and function from the key, and also the full queryKey, funcArgs and options
   */
  const resolveFull = (key: QueryKey, args: unknown[]) => {
    const { service, func } = resolveBasic(key)

    // Args are listed as: ...funcArgs, options?
    // func.length gives us the number of expected args for the function
    // So we can use that to split the args array
    const funcArgs = args.splice(0, func.length)
    // The remaining arg is the options object
    const [options = {}] = args as [Record<string, unknown>]

    // Assert that options is an object
    if (typeof options !== "object") {
      throw new Error(`Expected options to be an object, got ${typeof options}`)
    }

    return {
      service,
      func,
      queryKey: [...key, ...funcArgs],
      funcArgs,
      options,
    }
  }

  /**
   * Builds the query args for useQuery and useSuspenseQuery
   */
  const buildQueryArgs = (key: QueryKey, args: unknown[]) => {
    const { queryKey, service, func, funcArgs, options } = resolveFull(key, args)

    return {
      queryKey,
      queryFn: () => func(...funcArgs),

      // Default options can be declared in the service file:
      // ```
      //  export const queryOptions: QueryObserverOptions = { ... }
      // ```
      //
      // Options can also be set per function. For the function `getAll`:
      // ```
      //  export const getAllQueryOptions: QueryObserverOptions = { ... }
      // ```
      ...service["queryOptions"],
      ...service[`${key[1]}QueryOptions`],
      ...options,
    }
  }

  // The returned api object
  // Note: To resolve api methods all of this has to be casted. See Api type for docs
  return nestedProxy((key) => ({
    useQuery: (...args: unknown[]) => {
      const options = buildQueryArgs(key, args)

      // Validate options for suspense queries
      const { suspense, enabled } = options
      if (suspense === true) {
        if (typeof enabled !== "boolean") {
          throw new Error(
            "When using suspense queries, 'enabled' must be explicitly set to true or false. Use useSuspenseQuery directly if you want the query to always be enabled."
          )
        }

        // When suspense=true, enabled is ignored, so we set the hidden suspense option based on enabled
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        options.suspense = enabled
      }

      return useQuery(options)
    },

    useSuspenseQuery: (...args: unknown[]) => useSuspenseQuery(buildQueryArgs(key, args)),

    queryOptions: (...args: unknown[]) => buildQueryArgs(key, args),

    useIsFetching: () => useIsFetching({ queryKey: key }) !== 0,

    useIsMutating: () => useIsMutating({ mutationKey: key }) !== 0,

    useMutation: (options = {}) => {
      const { func } = resolveBasic(key)

      return useMutation({
        mutationKey: key,
        mutationFn: async (args: unknown[]) => await func(...args),
        ...options,
      })
    },

    invalidateQueries: (...args: unknown[]) => {
      const { queryKey, options } = resolveFull(key, args)

      return queryClient.invalidateQueries({ queryKey, ...options })
    },

    removeQueries: (...args: unknown[]) => {
      const { queryKey, options } = resolveFull(key, args)

      return queryClient.removeQueries({ queryKey, ...options })
    },
  })) as Api<TServiceDictionary>
}
